import { MODULES, type ModuleId, type ModuleMeta, type OverrideMode } from '@tahi/core';
import { useEffect, useState } from 'react';
import { ApiError } from '../../data/api';
import { Button } from '../../ui/Button';
import { Segmented } from '../../ui/Segmented';
import { Sheet } from '../../ui/Sheet';
import { adminApi, adminErrorText, type AdminUser } from './adminApi';
import { expiresIn, OneTimeLink } from './OneTimeLink';

const MODES: { value: OverrideMode; label: string }[] = [
  { value: 'default', label: 'Default' },
  { value: 'forced_on', label: 'On' },
  { value: 'forced_off', label: 'Off' },
];
const MODULE_ROWS = (Object.values(MODULES) as ModuleMeta[]).filter((m) => m.id !== 'today');
/** The owner's account never gets sample data (the Worker refuses it too). */
const OWNER_EMAIL = 'johnivanpuayap@gmail.com';

/** Test accounts only: never super admins or the owner. */
export function canHaveSampleData(user: AdminUser): boolean {
  return user.role !== 'super_admin' && user.email.toLowerCase() !== OWNER_EMAIL;
}

type SampleStep = { kind: 'confirm' } | { kind: 'replace'; message: string };

/** "Sample data" with a confirm sheet; asks again before replacing earlier sample data. */
function SampleDataAction({ user }: { user: AdminUser }) {
  const [step, setStep] = useState<SampleStep | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function fill(replace: boolean) {
    setBusy(true);
    setError(null);
    try {
      const r = await adminApi.fillSampleData(user.id, replace);
      setStep(null);
      setDone(
        r.replaced > 0
          ? `Replaced ${r.replaced} sample entries with ${r.entries} new ones. Their devices get them on the next sync.`
          : `Added ${r.entries} sample entries. Their devices get them on the next sync.`,
      );
    } catch (err) {
      if (err instanceof ApiError && err.code === 'has_sample_data') {
        setStep({ kind: 'replace', message: err.message });
      } else {
        setStep(null);
        setError(adminErrorText(err));
      }
    } finally {
      setBusy(false);
    }
  }

  const replacing = step?.kind === 'replace';
  return (
    <div className="flex flex-col gap-2">
      <Button
        onClick={() => {
          setDone(null);
          setStep({ kind: 'confirm' });
        }}
      >
        Sample data
      </Button>
      {done ? (
        <p role="status" className="text-sm text-muted">
          {done}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <Sheet
        open={step !== null}
        onOpenChange={(open) => !open && setStep(null)}
        title={replacing ? 'Replace sample data?' : 'Fill with sample data?'}
        description={
          replacing
            ? `${step.message} The old sample entries are deleted (they stay in history); entries the user made are kept.`
            : `About three months of Spending entries for ${user.email}, with Spending turned on and a Today layout. For test accounts only.`
        }
        footer={
          <div className="flex gap-2">
            <Button
              variant="primary"
              disabled={busy}
              onClick={() => void fill(replacing)}
              className="flex-1"
            >
              {replacing ? 'Replace' : 'Fill'}
            </Button>
            <Button onClick={() => setStep(null)}>Cancel</Button>
          </div>
        }
      >
        <p className="text-sm text-muted">Every change here goes into the audit log.</p>
      </Sheet>
    </div>
  );
}

function UserCard({
  user,
  onOverride,
}: {
  user: AdminUser;
  onOverride(moduleId: ModuleId, mode: OverrideMode): void;
}) {
  const [link, setLink] = useState<{ url: string; expiresAt: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  return (
    <li
      aria-label={user.email}
      className="flex flex-col gap-3 rounded-[24px] border border-divider bg-surface-2 p-4"
    >
      <div>
        <p className="font-medium">{user.email}</p>
        <p className="text-xs text-muted">{user.role === 'super_admin' ? 'Super admin' : 'User'}</p>
      </div>
      <ul className="flex flex-col gap-2">
        {MODULE_ROWS.map((m) => {
          const view = user.modules.find((v) => v.id === m.id);
          return (
            <li key={m.id} className="flex flex-wrap items-center justify-between gap-2">
              {/* Name over status, so the longest names still fit beside the control at 390px. */}
              <div className="min-w-0">
                <p className="text-sm">{m.name}</p>
                <p className="text-xs text-muted">{view?.enabled ? 'turned on' : 'turned off'}</p>
              </div>
              <Segmented<OverrideMode>
                label={`${m.name} for ${user.email}`}
                value={view?.override ?? 'default'}
                options={MODES}
                onChange={(mode) => onOverride(m.id, mode)}
              />
            </li>
          );
        })}
      </ul>
      <div className="flex flex-col gap-2">
        <Button
          onClick={async () => {
            setError(null);
            try {
              setLink(await adminApi.createResetLink(user.id));
            } catch (err) {
              setError(adminErrorText(err));
            }
          }}
        >
          Create reset link
        </Button>
        {link ? (
          <OneTimeLink
            url={link.url}
            note={`Send this link to ${user.email}. It works once and expires ${expiresIn(link.expiresAt)}. It won't be shown again.`}
          />
        ) : null}
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </div>
      {canHaveSampleData(user) ? <SampleDataAction user={user} /> : null}
    </li>
  );
}

export function UsersPanel() {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminApi.listUsers().then(setUsers, (err: unknown) => setError(adminErrorText(err)));
  }, []);

  async function setOverride(user: AdminUser, moduleId: ModuleId, mode: OverrideMode) {
    setUsers(
      (list) =>
        list?.map((u) =>
          u.id === user.id
            ? {
                ...u,
                modules: u.modules.map((v) =>
                  v.id === moduleId
                    ? { ...v, override: mode, managedByAdmin: mode !== 'default' }
                    : v,
                ),
              }
            : u,
        ) ?? null,
    );
    try {
      await adminApi.setOverride(user.id, moduleId, mode);
    } catch (err) {
      setError(adminErrorText(err));
      setUsers(await adminApi.listUsers().catch(() => null));
    }
  }

  if (error)
    return (
      <p role="alert" className="text-sm text-destructive">
        {error}
      </p>
    );
  if (!users)
    return (
      <p role="status" className="text-muted">
        Loading users…
      </p>
    );
  return (
    <ul className="flex flex-col gap-3">
      {users.map((u) => (
        <UserCard
          key={u.id}
          user={u}
          onOverride={(moduleId, mode) => void setOverride(u, moduleId, mode)}
        />
      ))}
    </ul>
  );
}
